import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import { Box } from "@mui/material";

import { PageLayout } from "../../layouts/PageLayout";
import { CardLayout, Invoice, PageHeader } from "../../components/v3";
import { DownloadAndPrint } from "../../components/DownloadAndPrint";
import { get_date } from "../../components/generalComponets";
import { PurchaseService } from "../../services";
import { getDateAndTime } from "../../utils/formatters";

interface UserInfo {
  firstName: string;
  lastName: string;
}

interface ContractDetailsData {
  startDate?: string;
  endDate?: string;
  createdBy?: UserInfo;
  createdAt?: string;
  updatedBy?: UserInfo;
  updatedAt?: string;
  title?: string;
  subcontractor?: { name: string };
  project?: { name: string };
  contractValue?: string | number;
}

export const ContractDetails = () => {
  const [invoiceData, setInvoiceData] = useState<Partial<ContractDetailsData>>({});

  const { contractId } = useParams();
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchData(contractId);
  }, [contractId]);

  const fetchData = async (id) => {
    try {
      const { data: response } = await PurchaseService.getContractById(id);
      setInvoiceData(response);
    } catch (error) {
      toast.error("Failed to fetch contract details.");
      console.error("Error fetching contract details:", error);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });
  return (
    <PageLayout>
      {/* header section */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <PageHeader title="Contract Details" />
      </Box>

      {/* table section */}
      <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
        <CardLayout>
          <div ref={printRef}>
            <Invoice
              title="Contract Details"
              sectionA={[
                { label: "Start Date", value: get_date(invoiceData?.startDate, "DD-MM-YYYY") },
                { label: "End Date", value: get_date(invoiceData?.endDate, "DD-MM-YYYY") },
              ]}
              sectionB={[
                {
                  label: "Created By",
                  value: invoiceData?.createdBy
                    ? `${invoiceData?.createdBy?.firstName} ${invoiceData?.createdBy?.lastName} (${getDateAndTime(
                        invoiceData?.createdAt,
                      )})`
                    : "N/A",
                },
                invoiceData?.updatedBy && {
                  label: "Last Updated By",
                  value: `${invoiceData?.updatedBy?.firstName ?? ""} ${invoiceData?.updatedBy?.lastName ?? ""} (${getDateAndTime(
                    invoiceData?.updatedAt,
                  )})`,
                },
              ].filter(Boolean)}
              sectionC={[
                { label: "Title", value: invoiceData?.title },
                { label: "Subcontractor", value: invoiceData?.subcontractor?.name },
              ]}
              sectionD={[
                { label: "Project", value: invoiceData?.project?.name },
                { label: "Amount", value: invoiceData?.contractValue ? `₹ ${invoiceData?.contractValue}` : "-" },
              ]}
            />
          </div>
        </CardLayout>
      </Box>

      <DownloadAndPrint handlePrint={handlePrint} printRef={printRef} fileName={"ContractDetails.pdf"} />
    </PageLayout>
  );
};
